import { type FC, memo, useEffect, useState } from 'react';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { TopologyGraphPreset } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { selectTopologyPresetModified } from 'components/Graph/GraphSidePanel/selectors/selectTopologyPresetModified';
import { addTopologyPreset } from 'components/Graph/commands/settings/addTopologyPreset';
import { deleteTopologyPreset } from 'components/Graph/commands/settings/deleteTopologyPreset';
import { discardTopologyPresetChanges } from 'components/Graph/commands/settings/discardTopologyPresetChanges';
import { exitTopologyPresetEditMode } from 'components/Graph/commands/settings/exitTopologyPresetEditMode';
import { resetTopologyPreset } from 'components/Graph/commands/settings/resetTopologyPreset';
import { updateTopologyPreset } from 'components/Graph/commands/settings/updateTopologyPreset';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { useLeaveRoute } from 'containers/LeaveDialogue/useLeaveRoute';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useGraphContextSelectors } from '../GraphContext';
import { closeLeavingDialog } from '../commands/closeLeavingDialog';
import { continueFromLeavingDialog } from '../commands/leavingDialog/continueFromLeavingDialog';
import { GraphAddPresetInput } from './GraphAddPresetInput';
import { GraphPresetLeavingDialog } from './GraphPresetLeavingDialog';
import { DELETE_PRESET_CONFIRM_DIALOG } from './constants';
import { selectPresets } from './selectors/selectPresets';

const BareGraphPresetControls: FC = () => {
  const msg = useLocale();
  const [saveAsFormOpen, setSaveAsFormOpen] = useState<boolean>(false);
  const ctxProps = useGraphContextSelectors({
    presetDraftId: prop('layoutSettings', 'presetDraft', 'id'),
    presetDraftState: prop('layoutSettings', 'presetDraftState'),
    dispatch: prop('dispatch'),
    isFactoryPreset: (s) => s.layoutSettings.presetDraft.factory,
    leavingDialog: prop('leavingDialog'),
    presetModified: selectTopologyPresetModified,
    presets: selectPresets,
  });
  const deletePresetConfirmDialog = useConfirmDialog({
    closeOnUnmount: true,
    name: DELETE_PRESET_CONFIRM_DIALOG,
  });
  const leaveRoute = useLeaveRoute(ctxProps.presetModified);

  const handleAddPreset = useStableCallback(
    async (name: TopologyGraphPreset['name']) => {
      await ctxProps.dispatch(addTopologyPreset, { name });
      ctxProps.dispatch(exitTopologyPresetEditMode);
      setSaveAsFormOpen(false);
    },
  );

  const handleAddPresetButtonClick = () => {
    if (ctxProps.presetDraftState === 'idle') {
      setSaveAsFormOpen(true);
    }
  };

  const handleResetPresetButtonClick = () => {
    ctxProps.dispatch(resetTopologyPreset);
  };

  const handleUpdatePresetButtonClick = async () => {
    if (ctxProps.presetDraftState === 'idle') {
      await ctxProps.dispatch(updateTopologyPreset);
      ctxProps.dispatch(exitTopologyPresetEditMode);
    }
  };

  const discardAndExit = () => {
    ctxProps.dispatch(discardTopologyPresetChanges);
    ctxProps.dispatch(exitTopologyPresetEditMode);
  };

  const handleDeleteButtonClick = () => {
    deletePresetConfirmDialog.toggleConfirmDialog();
  };

  const handleDeleteConfirm = useStableCallback(async () => {
    await ctxProps.dispatch(deleteTopologyPreset, {
      id: ctxProps.presetDraftId,
    });
    deletePresetConfirmDialog.closeConfirmDialog();
  });

  const onDiscardClickLeavingDialog = () => {
    ctxProps.dispatch(discardTopologyPresetChanges);
    ctxProps.dispatch(continueFromLeavingDialog);
  };

  const onSaveClickLeavingDialog = async () => {
    if (ctxProps.isFactoryPreset) {
      ctxProps.dispatch(closeLeavingDialog);
      setSaveAsFormOpen(true);
      leaveRoute.cancel();
    } else {
      await ctxProps.dispatch(updateTopologyPreset);
      ctxProps.dispatch(continueFromLeavingDialog);
      leaveRoute.leave();
    }
  };

  const getLeaveDialogMsg = () => {
    const leavingDialogMsg = {
      ...msg.canvasGraph.graphSidePanel.layoutSettings.leaveDialog,
    };
    if (ctxProps.isFactoryPreset) {
      leavingDialogMsg.header =
        msg.canvasGraph.graphSidePanel.layoutSettings.leaveDialog.editPreset.header;
      leavingDialogMsg.subHeader =
        msg.canvasGraph.graphSidePanel.layoutSettings.leaveDialog.editPreset.subHeader;
      leavingDialogMsg.save =
        msg.canvasGraph.graphSidePanel.layoutSettings.leaveDialog.editPreset.save;
    } else {
      leavingDialogMsg.save =
        msg.canvasGraph.graphSidePanel.layoutSettings.leaveDialog.saveLeave;
    }
    return leavingDialogMsg;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: triggered on preset draft change
  useEffect(() => {
    setSaveAsFormOpen(false);
  }, [ctxProps.presetDraftId]);

  return (
    <>
      <ConfirmDialog
        confirmDisabled={ctxProps.presetDraftState !== 'idle'}
        id={DELETE_PRESET_CONFIRM_DIALOG}
        keepOnConfirm
        msg={
          msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
            .deleteConfirm
        }
        onClickConfirm={handleDeleteConfirm}
        onClickSecondaryConfirm={() => {
          deletePresetConfirmDialog.closeConfirmDialog();
        }}
        btnConfirmVariant='primary'
        btnConfirmAccent='negative'
        btnCancelVariant='secondary'
        btnCancelAccent='positive'
      />
      {(ctxProps.leavingDialog.open || leaveRoute.blocked) && (
        <GraphPresetLeavingDialog
          onDiscardClick={onDiscardClickLeavingDialog}
          onSaveClick={onSaveClickLeavingDialog}
          leaveRoute={leaveRoute}
          msg={getLeaveDialogMsg()}
        />
      )}
      <div className='graph-preset-controls flex justify-between p-5'>
        {saveAsFormOpen ? (
          <GraphAddPresetInput
            onSaveClick={handleAddPreset}
            onCloseClick={() => {
              setSaveAsFormOpen(false);
            }}
          />
        ) : (
          <>
            <Button
              variant='secondary'
              type='button'
              className='graph-preset-controls__button'
              onClick={discardAndExit}
            >
              <span>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
                    .btnCancel
                }
              </span>
            </Button>
            <Button
              variant='secondary'
              type='button'
              className='graph-preset-controls__button'
              onClick={handleResetPresetButtonClick}
              disabled={!ctxProps.presetModified}
            >
              <span>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
                    .btnReset
                }
              </span>
            </Button>
            <Button
              variant='primary'
              accent='negative'
              type='button'
              className='graph-preset-controls__button'
              onClick={handleDeleteButtonClick}
              disabled={ctxProps.isFactoryPreset}
            >
              <span>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
                    .btnDelete
                }
              </span>
            </Button>
            <Button
              variant='primary'
              accent='positive'
              type='button'
              className='graph-preset-controls__button'
              onClick={handleAddPresetButtonClick}
            >
              <span>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
                    .btnSaveAs
                }
              </span>
            </Button>
            <Button
              variant='primary'
              accent='positive'
              type='button'
              className='graph-preset-controls__button'
              onClick={handleUpdatePresetButtonClick}
              disabled={ctxProps.isFactoryPreset || !ctxProps.presetModified}
            >
              <span>
                {
                  msg.canvasGraph.graphSidePanel.layoutSettings.presetsControls
                    .btnSave
                }
              </span>
            </Button>
          </>
        )}
      </div>
    </>
  );
};

export const GraphPresetControls = memo(BareGraphPresetControls);
